import { useContext, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { StatusLabel } from '@shared/components';
import { SignInContext } from '@modules/users';
import { TFolder, useFolder } from '@modules/items';

import { useLiterature } from '../api';
import LiteratureContainer from '../components/LiteratureContainer';

// Flat route: `/literature/:id`. Resolves `organization.folders.literature`
// for the nav drawer and breadcrumbs (backend permission scope), and the
// literature detail itself (`useLiterature`) in parallel.
const Literature = () => {
  const { id } = useParams<{ id: string }>();
  const { organization } = useContext(SignInContext);
  const isEnabled = !!organization.featureSettings?.literatures?.enabled && !!organization.folders.literature;

  const { error: folderError, data: folderData } = useFolder(organization.folders.literature, { enabled: isEnabled });
  const { error: literatureError, data: literatureData } = useLiterature(id as string, { enabled: isEnabled });

  const error = folderError || literatureError;
  const data = useMemo(() => {
    if (folderData && literatureData) {
      return { folder: folderData as TFolder, ...literatureData };
    }
    return null;
  }, [folderData, literatureData]);

  if (!isEnabled) return <Navigate to="/" replace />;

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading literature..."
      error={error}
      errorMessage="Loading literature failed!"
      render={(resolved) => (
        <LiteratureContainer
          folder={resolved.folder}
          literature={resolved.literature}
          permissions={resolved.permissions}
          resolvedItems={resolved.resolvedItems}
        />
      )}
    />
  );
};

export default Literature;
